import React from 'react';

import RefreshIcon from '@mui/icons-material/Refresh';
import {
  TableContainer,
  Table,
  TableRow,
  TableCell,
  TableFooter,
  TablePagination,
  TableHead,
  Paper,
  Button,
  Checkbox,
} from '@mui/material';

import Body from './Body';
import Filter from './Filter';
import TablePaginationActions from './TablePaginationActions';

import type { TableBaseColumnType, TableBaseRowType } from './Body';
import type { ColumnNamesType } from './Filter';

/** テーブルアクション */
export type TableActionType = {
  /** アイコン */
  icon: JSX.Element;
  /** アクション */
  action: (r: TableBaseRowType) => void;
  /** タイトル */
  title: string,
};

/**
 * プロパティ
 */
export interface TableBaseProps {
  /** Rows */
  rows: TableBaseRowType[];
  /** Columns */
  columns: TableBaseColumnType[];
  /** アクション */
  actions?: TableActionType[];
  /** 追加アクション */
  addAction?: () => void,
  /** チェック */
  onCheck?: (checked: boolean, name: string) => void,
  /** 全部チェックするか */
  isCheckAll?: boolean,
  /** 更新 */
  refreshAction?: () => void,
  /** フィルタを表示するか */
  isShowFilter?: boolean,
  /** ページネーションを表示するか */
  isPagination?: boolean,
  /** フッターを表示するか */
  isShowFooter?: boolean,
  /** サイズ */
  size?: 'small' | 'medium',
  /** ホバー */
  hover?: boolean
  /** 行クリック */
  onRowClick?: (row: TableBaseRowType) => void;
}

const TableBase: React.FC<TableBaseProps> = ({
  rows,
  columns,
  actions = [],
  addAction,
  onCheck,
  onRowClick,
  isCheckAll = false,
  refreshAction,
  isShowFilter = true,
  isPagination = true,
  isShowFooter = true,
  size = 'medium',
  hover = false,
}) => {
  const searchIds: string[] = columns.map((v) => v.id);
  const [searchId, setSearchId] = React.useState(searchIds.length > 0 ? searchIds[0] : '');
  const [filter, setFilter] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(isPagination ? 25 : -1);
  const columnNames: ColumnNamesType = {};
  for (let i = 0; i < columns.length; i += 1) {
    columnNames[columns[i].id] = columns[i].label;
  }
  const tableRows = [];
  if (searchIds.length > 0 && filter !== '') {
    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i][searchId];
      if (row.indexOf(filter) > -1) {
        tableRows.push(rows[i]);
      }
    }
  } else {
    for (let i = 0; i < rows.length; i += 1) {
      tableRows.push(rows[i]);
    }
  }
  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  let gridTemplateColumns = '1fr';
  if (isShowFilter) {
    gridTemplateColumns = searchIds.length > 1 ? '1fr 370px' : '1fr 270px';
  }
  return (
    <div>
      {(addAction || refreshAction || isShowFilter) && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns,
            gap: '5px',
          }}
        >
          <div>
            {refreshAction && (
              <Button
                variant="contained"
                onClick={() => refreshAction()}
                style={{ marginBottom: '5px', marginRight: '15px' }}
                startIcon={<RefreshIcon />}
              >
                再読込
              </Button>
            )}
            {addAction && (
              <Button
                variant="contained"
                onClick={() => addAction()}
                style={{ marginBottom: '5px' }}
              >
                新規登録
              </Button>
            )}
          </div>
          {isShowFilter && (
            <Filter
              searchId={searchId}
              searchIds={searchIds}
              setSearchId={setSearchId}
              columnNames={columnNames}
              filter={filter}
              setFilter={setFilter}
            />
          )}
        </div>
      )}
      <TableContainer component={Paper}>
        <Table size={size}>
          <TableHead>
            <TableRow>
              {onCheck && (
                <TableCell
                  key="h-check"
                  align="center"
                  style={{ width: '24px' }}
                >
                  <Checkbox
                    color="primary"
                    checked={isCheckAll}
                    onChange={(e) => onCheck(e.target.checked, '')}
                  />
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell
                  key={`h-${column.id}`}
                  align={column.align}
                  style={{
                    minWidth: column.minWidth,
                    width: column.width,
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
              {actions.length > 0 && (
                <TableCell key="actions" align="center">操作</TableCell>
              )}
            </TableRow>
          </TableHead>
          <Body
            tableRows={tableRows}
            page={page}
            rowsPerPage={rowsPerPage}
            columns={columns}
            actions={actions}
            onCheck={onCheck}
            hover={hover}
            onRowClick={onRowClick}
          />
          {isShowFooter && (
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, { label: '全表示', value: -1 }]}
                  colSpan={3}
                  count={tableRows.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  labelRowsPerPage="表示件数/ページ"
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>
    </div>
  );
};

export default TableBase;
